import type { ReactNode } from 'react';
import { useAuth } from './useAuth';
import { AccessDenied } from './AccessDenied';
import { userHasPermission } from './hasPermission';
import type { GrantedPermissionLevel } from './types';

interface PermissionGateProps {
  /** Klíč uzlu katalogu vyžadovaný pro zobrazení obsahu (např. 'products.venua-planner'). */
  permission: string;
  /** Minimální požadovaná úroveň; default `read` (zobrazení agendy). */
  minLevel?: GrantedPermissionLevel;
  children: ReactNode;
}

/**
 * Vyrenderuje `children` jen tehdy, když má přihlášený uživatel na uzlu alespoň `minLevel`.
 * Jinak zobrazí jednotnou hlášku {@link AccessDenied}. Slouží jako poslední obrana
 * obsahu (agendy) i při přímém vstupu přes URL nebo historii — nestačí skrýt
 * položku v menu. Backend endpointy oprávnění vynucují nezávisle.
 *
 * Dokud autentizace probíhá (`status === 'loading'`), nerenderuje nic, aby
 * neproblikla hláška před načtením oprávnění.
 */
export function PermissionGate({ permission, minLevel = 'read', children }: PermissionGateProps) {
  const auth = useAuth();

  if (auth.status === 'loading') return null;

  const allowed =
    auth.status === 'authenticated' && userHasPermission(auth.user, permission, minLevel);
  if (!allowed) return <AccessDenied />;

  return <>{children}</>;
}
